import React, { useContext, useMemo } from 'react';
import { Box, Grid } from '@mui/material';
import classNames from 'classnames';
import { useI18nContext } from '@procore/core-react';
import { type Project, type ProjectTile } from '../../types/project';
import { useStyles } from './PublicProjectLayout.styles';
import AppContext from '../AppContext/AppContext';
import BreadcrumbContainer, {
  type BreadcrumbItem,
} from '../Breadcrumbs/BreadcrumbContainer';
import PublicProjectHeader from './PublicProjectHeader';
import PublicProjectAbout from './PublicProjectAbout';
import PublicProjectContactInfo from './PublicProjectContactInfo';
import PublicProjectBidDocuments from './PublicProjectBidDocuments';
import PublicProjectInformation from './PublicProjectInformation';
import PublicProjectInstructions from './PublicProjectInstructions';
import SimilarProjects from './SimilarProjects';
import {
  getNdaProjectDisplay,
  resolveProjectTitle,
} from '../../helpers/nda-display';

interface Props {
  project: Project;
  nearbyProjects: ProjectTile[];
  sameCompanyProjects: ProjectTile[];
  breadcrumbs: BreadcrumbItem[];
}

const PublicProjectLayout = ({
  project,
  nearbyProjects,
  sameCompanyProjects,
  breadcrumbs,
}: Props) => {
  const I18n = useI18nContext();
  const { isUserLoggedIn } = useContext(AppContext);
  const classes = useStyles();

  const ndaDisplay = getNdaProjectDisplay(project);

  const profileBreadcrumb = useMemo(() => {
    const parsedProjectName = resolveProjectTitle(
      project,
      ndaDisplay,
      I18n.t('components.breadcrumbs.nda_project_title')
    );
    return (
      <BreadcrumbContainer
        crumbs={[...breadcrumbs, { name: parsedProjectName }]}
        projectsSearch={true}
      />
    );
  }, [breadcrumbs]);

  return (
    <Box className={classes.container} data-test-id="public-profile-container">
      <Grid item xs={12}>
        <Box pr={3}>{profileBreadcrumb}</Box>
      </Grid>
      <div className={classes.topContainer}>
        <div className={classes.leftSection}>
          <PublicProjectHeader project={project} />
          {!ndaDisplay.isNda && (
            <div id="about">
              <PublicProjectAbout project={project} />
            </div>
          )}
        </div>
        <div
          className={classNames(
            classes.rightSection,
            ndaDisplay.isNda ? classes.isNdaProject : ''
          )}
        >
          <PublicProjectContactInfo project={project} />
          {!ndaDisplay.isNda && <PublicProjectBidDocuments project={project} />}
        </div>
      </div>
      {isUserLoggedIn && (
        <div className={classes.middleContainer}>
          <PublicProjectInformation project={project} />
          <PublicProjectInstructions
            projectBiddingInstruction={project.biddingInstructions}
          />
        </div>
      )}
      {nearbyProjects?.length > 0 && (
        <SimilarProjects
          title={I18n.t('components.similar_projects.nearby_title')}
          description={
            project.location.city
              ? I18n.t('components.similar_projects.nearby_description', {
                  city: project.location.city,
                })
              : I18n.t('components.similar_projects.nearby_description_no_city')
          }
          similarProjects={nearbyProjects}
        />
      )}
      {sameCompanyProjects?.length > 0 && (
        <SimilarProjects
          title={I18n.t('components.similar_projects.same_company_title', {
            company: project.companyName,
          })}
          description={I18n.t(
            'components.similar_projects.same_company_description'
          )}
          similarProjects={sameCompanyProjects}
        />
      )}
    </Box>
  );
};

export default PublicProjectLayout;
